import classNames from 'classnames'
import React, {forwardRef, InputHTMLAttributes, useState} from 'react'

import {Icons} from '../icons/Icon'
import AssistiveText from './components/AssistiveText'
import LeadingComponent from './components/LeadingComponent'
import TrailingComponent from './components/TrailingComponent'

export type InputTypes = 'text' | 'number' | 'email' | 'search' | 'password'

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  value: string;
  type?: InputTypes;
  label?: string;
  hasError?: boolean;
  errorMessage?: string;
  helperText?: string;
  fullWidth?: boolean;
  leadingIcon?: Icons;
  trailingIcon?: Icons;
  disabled?: boolean;
  searchSize?: 'sm' | 'lg';
}

const Input = forwardRef<HTMLInputElement, TextInputProps>(({
  value,
  label,
  type = 'text',
  hasError = false,
  errorMessage = '',
  helperText = '',
  fullWidth = true,
  leadingIcon,
  trailingIcon,
  disabled = false,
  searchSize = 'lg',
  ...rest
}, ref) => {
  const wrapperClassNames = classNames('relative inline-block', {
    'w-full': fullWidth
  })

  const showError = hasError || errorMessage?.trim() !== ''

  const inputClasses = classNames(
    'peer bg-white appearance-none font-normal text-base rounded-xl border focus:outline-none text-kwota-neutral-800 disabled:text-kwota-neutral-400 disabled:border-kwota-neutral-200',
    {
      'border-kwota-alert-400 focus:border-kwota-primary-500': showError,
      'border-kwota-neutral-300 hover:border-kwota-neutral-400 focus:border-kwota-primary-500': !showError,
      'w-full': fullWidth,
      'pl-11': leadingIcon,
      'pr-11': trailingIcon || type === 'password' || type === 'search',
      'h-12 px-4 py-3': type !== 'search',
      'h-13 px-4 py-3': type === 'search' && searchSize === 'lg',
      'h-9 py-1 px-3': type === 'search' && searchSize === 'sm'
    }
  )
  const labelClasses = classNames(
    'absolute transition-all duration-400 peer-focus:text-xs peer-focus:text-kwota-neutral-800 peer-focus:bg-kwota-neutral-100 peer-focus:px-1 peer-focus:rounded-sm peer-disabled:-top-2 peer-disabled:bg-kwota-neutral-100 peer-disabled:px-1 peer-disabled:rounded-sm peer-disabled:text-xs peer-disabled:text-kwota-neutral-400 peer-autofill:text-xs peer-autofill:text-kwota-neutral-800 peer-autofill:-top-2 peer-autofill:bg-kwota-neutral-100 peer-autofill:px-1 peer-autofill:rounded-sm',
    {
      'peer-focus:-top-2': type !== 'search',
      'peer-focus:left-4 peer-autofill:left-4 peer-disabled:left-4':
          type !== 'search' || (type === 'search' && searchSize === 'lg'),
      'peer-focus:left-3 peer-autofill:left-3 peer-disabled:left-3': type === 'search' && searchSize === 'lg',
      'peer-focus:ml-0 peer-disabled:ml-0 peer-autofill:ml-0': leadingIcon,
      'ml-8': leadingIcon && value === '',
      'cursor-text': value === '',
      'peer-invalid:text-xs peer-invalid:text-kwota-neutral-800 peer-invalid:-top-2 peer-invalid:bg-kwota-neutral-100 peer-invalid:px-1 peer-invalid:rounded-sm': value === '' && type === 'number',
      'peer-invalid:ml-0': leadingIcon && value === '' && leadingIcon,
      'top-3 left-4': value === '' && type!=='search',
      'top-3.5 left-4': value === '' && type==='search' && searchSize === 'lg',
      'top-1.5 left-3': value === '' && type==='search' && searchSize === 'sm',
      'text-kwota-neutral-500': value === '' && !showError,
      'text-kwota-alert-400': value === '' && showError,
      'text-xs -top-2 bg-kwota-neutral-100 px-1 rounded-sm ml-0': value !== '',
      'left-4': value !== '' && (type !== 'search' || (type === 'search' && searchSize === 'lg')),
      'left-3': value !== '' && type==='search' && searchSize === 'sm',
      'text-kwota-neutral-800': value !== '' && !showError,
      'text-kwota-alert-400 font-semibold': value !== '' && showError
    }
  )

  const [showValue, setShowValue] = useState(false)
  const toggleShowPassword = () => setShowValue(!showValue)

  return (
    <div className={wrapperClassNames}>
      <LeadingComponent leadingIcon={leadingIcon} disabled={disabled} />
      <div>
        <input
          {...rest}
          id={label}
          type={`${showValue ? 'text' : type}`}
          value={value}
          disabled={disabled}
          className={inputClasses}
          ref={ref}
        />
        {label && type !== 'search' &&
          <label htmlFor={label} className={labelClasses}>
            {label}
          </label>
        }
      </div>
      <AssistiveText
        errorMessage={errorMessage}
        helperText={helperText}
        disabled={disabled}
      />
      <TrailingComponent
        type={type}
        showValue={showValue}
        disabled={disabled}
        trailingIcon={trailingIcon}
        toggleShowPassword={toggleShowPassword}
      />
    </div>
  )
})

export default Input
